import { useCallback } from "react";
import { authFetch } from "./utils/authFetch";

export type CurrencyCode = "usd" | "cad" | string;

export type CreditByCurrency = Record<CurrencyCode, number>;

export type BillingSummary = {
  // subscription basics
  plan: 1 | 2 | 3 | null;
  status: string | null;
  nextChargeIso: string | null;

  // pending plan change
  pendingPlanChange?: {
    newPlan: number;
    effectiveOn: string; // ISO date
    amountRemainingCents?: number;
  };

  // cycle window & usage
  cycleStartUtc: string; // ISO
  nextResetUtc: string; // ISO
  cap: number;
  wonThisCycle: number;
  left: number;
  overageWins: number;
  overageFeePerWin: number; // dollars
  projectedOverageCharge: number; // dollars
  projectedOverageChargeCents: number; // cents

  // stripe credits (raw)
  creditCurrency: CurrencyCode;
  creditCents: number; // effective credits after projected overage (clamped >= 0)
  totalCreditCents: number; // sum of all currency buckets (raw from Stripe)
  creditByCurrency: CreditByCurrency;

  // upcoming invoice (Stripe)
  upcomingAmountDueCents: number | null;
  upcomingAmountRemainingCents: number | null;

  // local balance (DB)
  balance: number; // your local ledger (can be negative)

  // unified view (use these in UI)
  accountBalanceCents: number; // Stripe net credit - upcoming_amount_remaining + local balance adj
  accountCurrency: CurrencyCode; // usually same as creditCurrency
  hasNegativeBalance: boolean; // true if accountBalanceCents < 0
  availableToSpendCents: number; // max(0, creditCents - projectedOverageChargeCents)
};

export const useBillingApi = () => {
  const getSummary = useCallback(async (): Promise<BillingSummary> => {
    const res = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/payments/summary`
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<BillingSummary>;
  }, []);

  return { getSummary };
};
